import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, useLocation, useNavigate, useRevalidator } from 'react-router';
import { pushSubscriptionModifyEventToDataLayer } from '../../common/analytics';
import { paths } from '../../common/constants/pathVariables';
import { DialogType } from '../../common/enums';
import { changeSubscriptionMobilePbx } from '../../common/fetch';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import { somethingWentWrongMsg, startTimeCantBeGreaterMsg, t } from '../../common/i18n';
import { getSubscriptionUrlId } from '../../common/utils/subscriptionUtils';
import { SubscriptionStatusType } from '../../generated/api/subscriptionStatusType';
import { openDialog } from '../../public/site/dialogStore.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { createPbxWorkingHoursConfig, formatPayload, workingHourErrors } from './PbxAccordionUtils';
import { PbxDetailsEdit } from './PbxDetailsEdit';
import { PbxDetailsView } from './PbxDetailsView';
import type { PbxSettingFormData } from './PbxAccordionUtils';
import type { DialogParams } from '../../common/types/dialog';
import type { Subscription } from '../../generated/api/subscription';
import type { SubscriptionPbxConfiguration } from '../../generated/api/subscriptionPbxConfiguration';

import './PbxAccordionContent.scss';

interface PbxAccordionContentAttrs {
  hasPendingActions: boolean;
  pbxSolution: Subscription;
  subscription: Subscription;
  isEditing: boolean;
  setEditing: (isEditing: boolean) => void;
  hideEditButton?: boolean;
}

const createFormData = (subscriptionPbxConfiguration: SubscriptionPbxConfiguration): PbxSettingFormData => ({
  corporateNumber: subscriptionPbxConfiguration.corporateNumber,
  ...createPbxWorkingHoursConfig(subscriptionPbxConfiguration),
  connectToNumber: subscriptionPbxConfiguration.connectToNumber,
  corporateNumberRangeId: subscriptionPbxConfiguration.corporateNumberRangeId,
  extensionNumber: subscriptionPbxConfiguration.extensionNumber,
  delayForChainCalls: subscriptionPbxConfiguration.delayForChainCalls
    ? subscriptionPbxConfiguration.delayForChainCalls.toString()
    : undefined,
  busyAction: subscriptionPbxConfiguration.busyAction,
  pbxSolutionId: subscriptionPbxConfiguration.pbxSolutionId,
  extensionRangeId: subscriptionPbxConfiguration.extensionRangeId,
  corporateRangeId: subscriptionPbxConfiguration.corporateNumberRangeId,
  pbxType: subscriptionPbxConfiguration.pbxConfigurationDetails.pbxType,
});

export const PbxAccordionContent = ({
  pbxSolution,
  hasPendingActions,
  subscription,
  isEditing,
  setEditing,
  hideEditButton = false,
}: PbxAccordionContentAttrs) => {
  const [isSaving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const { mdmId } = useSearchParams<{ mdmId?: string }>();
  const { revalidate } = useRevalidator();

  const onShowDialog = (params: DialogParams) => openDialog(params);

  // When we have pbxConfiguration, checked in MobileSubscriptionAccordions, this always exists
  const subscriptionPbxConfiguration = subscription.details!.mobile!.pbxConfiguration!;

  const formValues = createFormData(subscriptionPbxConfiguration);
  const originalWorkingTimeNumbers = subscriptionPbxConfiguration?.workingHoursReachabilityChain;
  const originalOffWorkNumbers = subscriptionPbxConfiguration?.pbxConfigurationDetails.offWorkReachabilityChain;
  const methods = useForm({
    defaultValues: formValues,
    mode: 'onSubmit',
  });

  const onEdit = () => {
    if (subscription?.subscriptionStatus === SubscriptionStatusType.IN_ACTIVATION) {
      onShowDialog({ type: DialogType.MACD_FORBIDDEN_SUBSCRIPTION_IN_ACTIVATION });
      return;
    }
    if (hasPendingActions) {
      onShowDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING });
      return;
    }
    setEditing(true);
  };

  const onConfirm = async (values: PbxSettingFormData) => {
    pushSubscriptionModifyEventToDataLayer('vakio settings');
    const weekdayErrors = workingHourErrors(values?.workingHoursWeekdayStart, values.workingHoursWeekdayEnd);
    const weekEndErrors = workingHourErrors(values?.workingHoursWeekendStart, values.workingHoursWeekendEnd);
    if (weekdayErrors || weekEndErrors) {
      if (weekdayErrors) {
        methods.setError(`workingHoursWeekdayEnd`, { message: t.E9GS(startTimeCantBeGreaterMsg) });
      }
      if (weekEndErrors) {
        methods.setError(`workingHoursWeekendEnd`, { message: t.E9GS(startTimeCantBeGreaterMsg) });
      }
      return;
    }

    setSaving(true);

    try {
      const payload = formatPayload(values, subscriptionPbxConfiguration);
      await changeSubscriptionMobilePbx(payload, subscription.subscriptionId, mdmId);
      setSaving(false);
      setEditing(false);
      addNotification(t.T29V('Change order successful'), 'success');
      navigate(
        generatePath(paths.PS_MOBILE_SUBSCRIPTION, { subscriptionId: getSubscriptionUrlId(subscription) }) + search
      );
      // Refresh /pending-subscription-actions data to update subscription status
      revalidate();
    } catch (error) {
      addNotification(t.JDTO(somethingWentWrongMsg), 'error');
      setSaving(false);
      setEditing(false);
    }
  };

  return isEditing ? (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onConfirm)} noValidate>
        <PbxDetailsEdit
          pbxSolution={pbxSolution}
          setEditing={setEditing}
          isSaving={isSaving}
          originalOffWorkNumbers={originalOffWorkNumbers}
          originalWorkingTimeNumbers={originalWorkingTimeNumbers}
          subscriptionUserFriendlyId={subscription.subscriptionUserFriendlyId}
        />
      </form>
    </FormProvider>
  ) : (
    <PbxDetailsView formValues={formValues} onEdit={onEdit} hideEditButton={hideEditButton} />
  );
};
